/**
 * Route guard: redirects to /login if there is no authenticated user.
 *
 * Preserves the attempted path as `?next=` so /login can route back
 * after a successful sign-in.
 *
 * If the user is authenticated but ``must_change_password`` is true,
 * funnels them to /change-password — they cannot use any feature
 * route until the new password is set. Mirrors the backend gate.
 */

import { Box, CircularProgress } from '@mui/material';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from './useAuth';

interface RequireAuthProps {
  children: ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps) {
  const { user, isReady } = useAuth();
  const location = useLocation();

  if (!isReady) {
    return <FullPageSpinner />;
  }

  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  if (user.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  return <>{children}</>;
}

function FullPageSpinner() {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CircularProgress color="primary" />
    </Box>
  );
}
